import { Controller } from "@hotwired/stimulus"

// MC-834: board card selection.
//
// The generic drawer-layout controller (drawer_layout_controller.js) is
// deliberately content-agnostic and knows nothing about cards or selection
// (MC-825 / ADR-20260701053432). Card selection is board-specific, so it lives
// here — on the board root, which encloses both the columns and the drawer, so
// it can see the open panel and every card at once.
//
// State is DOM-derived, matching the drawer-layout's own philosophy; there is
// no client-side "selected key" to drift:
//   - The drawer is open when the drawer-layout root carries data-state="open".
//     Closing leaves the panel `src` in place, so open-ness is read from
//     data-state, not from the presence of a src.
//   - The selected card is the one whose data-task-key matches the task key in
//     the open task panel frame's `src` (the task_drawers/:task_key path).
//
// reconcile() re-derives selection from the DOM and re-applies it. It runs on:
//   - drawer open/close — the drawer-layout dispatches generic
//     `drawer-layout:open` / `drawer-layout:close` lifecycle events that bubble
//     to this root (a card click retargets and opens the panel, so `open` also
//     covers click-time selection and switching between cards).
//   - a live column morph — Boards::ColumnBroadcast morphs a column frame from
//     server HTML that never carries `.card-task--selected`, wiping any
//     client-applied ring; `turbo:frame-render` bubbling from the column frame
//     re-runs reconcile, so the ring survives the morph without flicker.
//
// Wiring (data-controller + data-action) lives on the board root in
// Boards::ShowComponent#data.
export default class extends Controller {
  connect() {
    // Idempotent: re-derive selection from the current DOM. Safe to re-run
    // after a Turbo Drive navigation or a frame swap.
    this.reconcile()
  }

  // Clear every card's selection ring, then — if the drawer is open — mark the
  // one card whose key matches the open panel. Bound to the drawer lifecycle
  // and column-morph events via data-action on the board root.
  reconcile() {
    const cards = this.cards()
    cards.forEach((card) => card.classList.remove("card-task--selected"))

    if (!this.drawerOpen()) return

    const key = this.openTaskKey()
    if (!key) return

    cards.find((card) => card.dataset.taskKey === key)?.classList.add("card-task--selected")
  }

  // Board cards live in the columns region; scope the query there so a card
  // rendered inside the drawer panel (e.g. a children list) never picks up the
  // board's selection ring.
  cards() {
    return Array.from(this.element.querySelectorAll(".card-board__columns .card-task"))
  }

  drawerOpen() {
    const root = this.panel()?.closest("[data-controller~='drawer-layout']")
    return root?.dataset.state === "open"
  }

  // The task key of the open panel, parsed from its task_drawers/:task_key
  // src. Null when the panel has never been pointed at a task.
  openTaskKey() {
    const src = this.panel()?.getAttribute("src")
    const match = src?.match(/task_drawers\/([A-Za-z][A-Za-z0-9]*-\d+)/)
    return match ? match[1] : null
  }

  panel() {
    return this.element.querySelector("#task-drawer")
  }
}
